// src/components/employee/EmployeeProfile.js
import React, { useState, useEffect } from 'react';
import {
    Edit2, Save, X
} from 'lucide-react';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useAuth } from '../../hooks/useAuth';
import Alert from '../common/Alert';
import EmployeeProfileView from '../common/EmployeeProfileView';
import { toISTDateString } from '../../utils/dateHelpers';

// Helper to format date for input[type="date"] - handles timezone conversion to IST
const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    // Use toISTDateString to get the date in IST timezone (YYYY-MM-DD format)
    const istDate = toISTDateString(dateStr);
    return istDate && istDate !== 'Invalid Date' ? istDate : '';
};

// Helper to format profile with properly formatted dates for edit mode
const formatProfileForEdit = (profile) => {
    if (!profile) return {};
    return {
        ...profile,
        date_of_birth: formatDateForInput(profile.date_of_birth),
        joining_date: formatDateForInput(profile.joining_date || profile.joiningDate),
        effective_date: formatDateForInput(profile.pay_package?.effective_date || profile.effective_date),
    };
};

const EmployeeProfile = () => {
    const { employees, updateEmployee } = useEmployees();
    const { user } = useAuth();

    // Get current logged-in user's profile from EmployeeContext
    const profile = employees.find(emp => emp.id === user?.id) || user;

    const [editMode, setEditMode] = useState(false);
    const [editedProfile, setEditedProfile] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (profile) {
            setEditedProfile(formatProfileForEdit(profile));
        }
    }, [profile]);

    const handleEdit = () => {
        setEditMode(true);
        setEditedProfile(formatProfileForEdit(profile));
    };

    const handleCancel = () => {
        setEditMode(false);
        setEditedProfile(formatProfileForEdit(profile));
        setMessage({ type: '', text: '' });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const result = await updateEmployee(profile.id, editedProfile);
            const pendingApproval = result?.pendingApproval;
            const responseMessage = result?.message || (pendingApproval
                ? 'Your changes were sent to the admin team for approval.'
                : 'Profile updated successfully');

            setMessage({ type: 'success', text: responseMessage });
            setEditMode(false);
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage({ type: 'error', text: 'Failed to update profile' });
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedProfile(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            setMessage({ type: 'error', text: 'Please upload a valid image file (JPEG, PNG)' });
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
            return;
        }

        setLoading(true);
        try {
            // TODO: Replace with actual API call when backend is ready
            // const formData = new FormData();
            // formData.append('photo', file);
            // const response = await api.post(`/api/employees/${profile.id}/photo`, formData, {
            //     headers: { 'Content-Type': 'multipart/form-data' }
            // });

            // For now, create a local preview URL
            const photoURL = URL.createObjectURL(file);
            const result = await updateEmployee(profile.id, { ...profile, photo: photoURL });
            const pendingApproval = result?.pendingApproval;
            const responseMessage = result?.message || (pendingApproval
                ? 'Photo update sent for admin approval.'
                : 'Photo uploaded successfully');
            setMessage({ type: 'success', text: responseMessage });
        } catch (error) {
            console.error('Error uploading photo:', error);
            setMessage({ type: 'error', text: 'Failed to upload photo. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Profile</h1>
                {!editMode ? (
                    <button
                        onClick={handleEdit}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                        <Edit2 className="w-4 h-4" />
                        Edit Profile
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            onClick={handleCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 flex items-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            {message.text && (
                <Alert type={message.type} message={message.text} onClose={() => setMessage({ type: '', text: '' })} />
            )}

            <EmployeeProfileView
                profile={profile}
                employees={employees}
                isEditable={true}
                editMode={editMode}
                editedProfile={editedProfile}
                onEditChange={handleInputChange}
                onPhotoUpload={handlePhotoUpload}
                isOwnProfile={true}
                currentUser={user}
            />
        </div>
    );
};

export default EmployeeProfile;
